<template>
  <PageWrap
    :heading="$t('promote.nav.links.feedback_widget')"
    :loading="loading"
  >
    <Flex
      direction="column"
      :gap="2"
    >
      <BaseText>{{ $t('admin.feedback_widget.description') }}</BaseText>
      <BaseCard :heading="$t('global.settings')">
        <Flex direction="column">
          <BaseSwitch
            v-model="widgetSize"
            :options="[
          {
            label: $t('admin.feedback_widget.size.large'),
            value: 'large'
          },
          {
            label: $t('admin.feedback_widget.size.small'),
            value: 'small'
          }
        ]"
            :label="$t('admin.feedback_widget.size.heading')"
            v-test="'feedback-widget-size'"
          />
          <div v-if="widgetSize === 'small'">
            <BaseInputLabel :text="$t('admin.feedback_widget.see_all_label')" />
            <BaseInput
              v-model="seeAllUrl"
              type="url"
              v-test="'feedback-widget-mini-link'"
            />
          </div>
        </Flex>
      </BaseCard>

      <BaseCard :heading="$t('admin.feedback_widget.widget_code')">
        <Flex direction="column">
          <BaseText>
            {{ $t('admin.feedback_widget.snippet_heading') }}:
          </BaseText>
          <BaseCodeField
            :code="snippetCode"
            v-test="'feedback-widget-snippet'"
          />
          <Flex>
            <BaseButton
              :href="helpCenterArticleUrl($t('admin.feedback_widget.learn_more_link'))"
              color="inverted"
            >
              {{ $t('global.learn_more') }}
            </BaseButton>
          </Flex>
        </Flex>
      </BaseCard>

      <BaseCard :heading="$t('global.preview')">
        <iframe
          :class="[
          $style.iframe,
          {
            [$style.small]: widgetSize === 'small'
          }
        ]"
          :src="iframeSrc"
          v-test="'feedback-widget-iframe'"
        />
      </BaseCard>
    </Flex>
  </PageWrap>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { useStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { useCompanyStore } from '@/stores/company';
import { useMicroSiteStore } from '@/stores/micro-site';
import PageWrap from '../PageWrap.vue';

const { companyId } = useCompanyStore();

const widgetSize = ref('large');
const widgetUrl = ref('');
const widgetUrlSmall = ref('');

const seeAllUrl = useStorage(`feedback_widget_link_${companyId}`, '');

// Migrate old non-company-specific storage to new company-specific key
const oldSeeAllUrl = useStorage('feedback_widget_link', '');
if (oldSeeAllUrl.value && !seeAllUrl.value) {
  seeAllUrl.value = oldSeeAllUrl.value;
  oldSeeAllUrl.value = '';
}

const { onResult, loading } = useQuery(gql`
  query getFeedbackWidget {
    feedbackWidget {
      feedbackUrl
      miniFeedbackUrl
    }
  }
`);

onResult(({ data: { feedbackWidget } }) => {
  widgetUrl.value = feedbackWidget.feedbackUrl;
  widgetUrlSmall.value = feedbackWidget.miniFeedbackUrl;

  if (!seeAllUrl.value) {
    seeAllUrl.value = widgetUrl.value;
  }
});

const { microSiteUrl } = storeToRefs(useMicroSiteStore());

const iframeSrc = computed(() => {
  if (widgetSize.value === 'small') {
    return `${widgetUrlSmall.value}?layout=embed&link=${seeAllUrl.value}`;
  } else {
    return `${widgetUrl.value}?layout=embed`;
  }
});

const snippetCode = computed(() => {
  if (widgetSize.value === 'small') {
    return (
      `<script src="https://cdn.salonized.com/widget.js" data-name="salonized" data-microsite-url="${microSiteUrl.value}"><` +
      `/script>
<div class="salonized-reviews-mini" data-link="${seeAllUrl.value}"></div>`
    );
  } else {
    return (
      `<script src="https://cdn.salonized.com/widget.js" data-name="salonized" data-microsite-url="${microSiteUrl.value}"><` +
      `/script>
<div class="salonized-reviews"></div>`
    );
  }
});
</script>

<style
  lang="scss"
  module
>
.iframe {
  border: 0;
  max-width: 100%;
  background-color: white;

  &.small {
    width: 400px;
  }

  &:not(.small) {
    width: 100%;
    height: 800px;
  }
}
</style>
